import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { Store } from '@ngrx/store';
import { CalendarIntegration } from '../../../../../../store/calendar-integration/calendar-integration.models';
import { CalendarIntegrationFetchRequest, CalendarIntegrationDisconnectRequest, CalendarIntegrationUpsertRequest } from '../../../../../../store/calendar-integration/calendar-integration.actions';
import { selectCalendarIntegrationLoading, selectCalendarIntegrations } from '../../../../../../store/calendar-integration/calendar-integration.selectors';


@Component({
  selector: 'app-calendar-integrations-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
  ],
  templateUrl: './calendar-integrations-dialog.component.html',
  styleUrl: './calendar-integrations-dialog.component.scss',
})
export class CalendarIntegrationsDialogComponent {
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogRef = inject(MatDialogRef<CalendarIntegrationsDialogComponent>);
  private readonly formBuilder = inject(FormBuilder);

  protected readonly loading = signal(false);
  protected readonly integrations = signal<CalendarIntegration[]>([]);

  protected readonly form = this.formBuilder.group({
    provider: ['google', [Validators.required]],
    account_email: [''],
    external_calendar_id: ['primary'],
    access_token: ['', [Validators.required]],
    refresh_token: [''],
    expires_at: [''],
    scopes: [''],
    external_user_id: [''],
  });

  constructor() {
    this.store.dispatch(CalendarIntegrationFetchRequest());

    this.store
      .select(selectCalendarIntegrationLoading)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((loading) => this.loading.set(!!loading));

    this.store
      .select(selectCalendarIntegrations)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((integrations) => this.integrations.set(integrations ?? []));
  }

  protected close(): void {
    this.dialogRef.close();
  }

  protected disconnect(provider: string): void {
    this.store.dispatch(CalendarIntegrationDisconnectRequest({ provider }));
  }

  protected submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const scopesRaw = this.form.controls.scopes.value?.trim() ?? '';
    const scopes = scopesRaw ? scopesRaw.split(',').map((value) => value.trim()).filter(Boolean) : [];

    this.store.dispatch(
      CalendarIntegrationUpsertRequest({
        payload: {
          provider: this.form.controls.provider.value ?? 'google',
          account_email: this.form.controls.account_email.value?.trim() ?? '',
          external_calendar_id: this.form.controls.external_calendar_id.value?.trim() ?? '',
          access_token: this.form.controls.access_token.value?.trim() ?? '',
          refresh_token: this.form.controls.refresh_token.value?.trim() ?? '',
          expires_at: this.form.controls.expires_at.value?.trim() ?? '',
          scopes,
          external_user_id: this.form.controls.external_user_id.value?.trim() ?? '',
        },
      }),
    );
  }
}

